import React, { useState, useEffect } from "react";
import { Select, DatePicker, Typography, Space, Skeleton } from "antd";
import dayjs from "dayjs";
import {
  TODAY,
  YESTERDAY,
  LAST_7_DAYS,
  THIS_MONTH,
  LAST_MONTH,
  LAST_6_MONTHS,
  THIS_YEAR,
  CUSTOM,
} from "../../../utils/constants";
import { minDate, maxDate } from "../../../utils/utils";
import useStyles from "./DatePicker.styles";

const { Option } = Select;
const { Text } = Typography;

const presets = [
  { label: TODAY, range: [dayjs(), dayjs()] },
  {
    label: YESTERDAY,
    range: [dayjs().subtract(1, "day"), dayjs().subtract(1, "day")],
  },
  { label: LAST_7_DAYS, range: [dayjs().subtract(6, "day"), dayjs()] },
  {
    label: THIS_MONTH,
    range: [dayjs().startOf("month"), dayjs()],
  },
  {
    label: LAST_MONTH,
    range: [
      dayjs().subtract(1, "month").startOf("month"),
      dayjs().subtract(1, "month").endOf("month"),
    ],
  },
  {
    label: LAST_6_MONTHS,
    range: [
      dayjs().subtract(6, "month").startOf("month"),
      dayjs().subtract(1, "month").endOf("month"),
    ],
  },
  {
    label: THIS_YEAR,
    range: [dayjs().startOf("year"), dayjs()],
  },
  { label: CUSTOM, range: [null, null] },
];

export default function DateRangeDatePickerWithPresets({
  text,
  isLoading,
  minStartDate,
  maxEndDate,
  setSelectedPresetDateRange,
  setSelectedStartDate,
  setSelectedEndDate,
  selectedStartDate,
  selectedEndDate,
} = {}) {
  const { classes } = useStyles();

  const defaultPreset = presets.find((p) => p.label === LAST_6_MONTHS);

  const [preset, setPreset] = useState(LAST_6_MONTHS);
  const [range, setRange] = useState(defaultPreset?.range ?? [null, null]);
  const [isTouched, setIsTouched] = useState(false);

  useEffect(() => {
    if (defaultPreset) {
      setSelectedStartDate(defaultPreset.range[0]);
      setSelectedEndDate(defaultPreset.range[1]);
      setSelectedPresetDateRange(LAST_6_MONTHS);
    }
  }, [
    defaultPreset,
    setSelectedEndDate,
    setSelectedPresetDateRange,
    setSelectedStartDate,
  ]);

  const parsedMinStartDate = minStartDate ? dayjs(minStartDate) : minDate;

  const parsedMaxEndDate = maxEndDate
    ? dayjs(maxEndDate).endOf("day")
    : maxDate;

  const handlePresetChange = (value) => {
    setIsTouched(true);
    setPreset(value);
    const selectedPreset = presets.find((p) => p.label === value);
    if (selectedPreset.label !== CUSTOM) {
      setRange(selectedPreset.range);
      setSelectedStartDate(selectedPreset.range[0]);
      setSelectedEndDate(selectedPreset.range[1]);
      setSelectedPresetDateRange(value);
    } else {
      // For CUSTOM preset, clear range or keep current
      setRange([null, null]);
      setSelectedStartDate(null);
      setSelectedEndDate(null);
      setSelectedPresetDateRange(value);
    }
  };

  const handleRangeChange = (dates) => {
    setIsTouched(true);
    if (dates && dates.length === 2) {
      // Use selected dates as-is (day precision)
      const start = dates[0] ?? null;
      const end = dates[1] ?? null;
      setRange([start, end]);
      setSelectedStartDate(start);
      setSelectedEndDate(end);
      setPreset(CUSTOM);
      setSelectedPresetDateRange(CUSTOM);
    } else {
      setRange([null, null]);
      setSelectedStartDate(null);
      setSelectedEndDate(null);
    }
  };

  return (
    <>
      {isLoading ? (
        <Skeleton.Input style={{ width: 180, height: 40 }} active />
      ) : (
        <Space align="start" size="middle" style={{ width: "100%" }}>
          <Select
            value={preset}
            onChange={handlePresetChange}
            className={classes.dateRangePresets}
            placeholder="Select Range"
            allowClear={false}
            size="middle"
          >
            {presets.map((p) => (
              <Option key={p.label} value={p.label}>
                {p.label}
              </Option>
            ))}
          </Select>
          {preset === CUSTOM && (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <DatePicker.RangePicker
                value={range}
                onChange={handleRangeChange}
                disabledDate={(current) => {
                  if (!current) return false;
                  return (
                    current.isBefore(parsedMinStartDate, "day") ||
                    current.isAfter(parsedMaxEndDate, "day")
                  );
                }}
                className={classes.dateRangeField}
                allowEmpty={[true, true]}
                placeholder={["Start Date", "End Date"]}
                size="middle"
                format="MMM DD, YYYY"
              />
              {(!preset || !selectedStartDate || !selectedEndDate) &&
                isTouched && (
                  <Text type="danger" style={{ fontSize: 12, marginTop: 4 }}>
                    {text} is required!
                  </Text>
                )}
            </div>
          )}
        </Space>
      )}
    </>
  );
}
